import _uniq from 'lodash.uniq'
import moment from 'moment-timezone'
import { formatCurrency, formatDate, formatTime } from '@smarttransit/common'
import { isAuthorized, addAlert } from '../../../utilities/helpers'

import {
  findTotalTransactionsPayout,
  findTransactionsPayout
} from '../../../services/passenger-transaction-logs-service'

import { findTransportationProfiles } from '@/services/transportation-profiles-service'

const REVENUE_COUNT_REQUEST_TIMEOUT_MS = 120000

export default {
  name: 'transportation-accounts-purchases',
  props: {
    signedInUser: Object,
    forceRootViewRefresh: Function,
    selectedAccount: Object
  },
  data () {
    return {
      apiInProgress: false,
      countInProgress: false,
      lastSearchRequestId: 0,
      countryCurrencies: {},
      selectedVehicles: [],
      dateFrom: null,
      dateTo: null,
      headers: [],
      transactions: [],
      // When null, total is unknown (use next/previous pagination only).
      totalTransactions: null,
      selectedPaidFilter: '',
      selectedLogType: null,
      logTypeFilterOptions: [
        { text: 'Purchased Ticket', value: 'purchased_ticket' },
        { text: 'Ad Hoc Revenue', value: 'ad_hoc_revenue' }
      ],
      adhocRevenueDialog: false,
      adhocRevenue: null,
      pagination: {
        descending: true,
        page: 1,
        rowsPerPage: 10,
        sortBy: 'dateCreated'
      }
    }
  },
  computed: {
    currentSelectedAccount () {
      return this.$props.selectedAccount
    },
    isAdmin () {
      if (this.$props.signedInUser) {
        return isAuthorized(this.$props.signedInUser, 'admin')
      }
    },
    isSuperAdmin () {
      if (this.$props.signedInUser) {
        return isAuthorized(this.$props.signedInUser, 'superadmin')
      }
    },
    isTotalTransactionsKnown () {
      return typeof this.totalTransactions === 'number'
    },
    tableTotalItems () {
      // Vuetify clamps `pagination.page` based on total-items.
      // Keep total-items non-zero while count is unknown.
      if (this.isTotalTransactionsKnown) {
        return this.totalTransactions
      }

      const rowsPerPage = Number(this.pagination && this.pagination.rowsPerPage) || 10
      const page = Number(this.pagination && this.pagination.page) || 1
      return page * rowsPerPage
    },
    canGoToPreviousPage () {
      return this.pagination.page > 1 && !this.apiInProgress
    },
    canGoToNextPage () {
      // With unknown totals, assume there may be more when current page is full.
      return this.transactions && this.transactions.length === this.pagination.rowsPerPage && !this.apiInProgress
    },
    selectedVehicleIds () {
      return (this.selectedVehicles || [])
        .map((vehicle) => vehicle && vehicle.id)
        .filter(Boolean)
    }
  },
  mounted () {
    if (this.$props.signedInUser) {
      this.isTransporter = this.$props.signedInUser.roles.find((o) => (o.name === 'transporter'))
    }

    this.headers = [
      {
        text: 'Amount',
        align: 'left',
        sortable: true,
        value: 'amount'
      },
      {
        text: 'Date',
        align: 'left',
        sortable: true,
        value: 'dateCreated'
      },
      {
        text: 'Type',
        align: 'left',
        sortable: true,
        value: 'logType'
      },
      {
        text: 'Payout Status',
        align: 'left',
        sortable: true,
        value: 'transportationUserTransactionLogId'
      },
      {
        text: 'Vehicle',
        align: 'left',
        sortable: false
      },
      {
        text: 'Notes',
        align: 'left',
        sortable: false
      }
    ]

    this.$store.getters.getCountryCurrencies.then((countryCurrencies) => {
      this.countryCurrencies = countryCurrencies
    })

    this.firstApiCalled = true
    this.searchTransactions()

    this.selectedAccountWatchHandle = this.$watch(() => { return this.$props.selectedAccount }, (newVal, oldVal) => {
      this.onSelectedAccountChanged(newVal)

      if (this.firstApiCalled && !this.apiInProgress && (this.$route.params.accountId || this.$route.params.accountId === 0)) {
        this.firstApiCalled = true
        this.searchTransactions()
      }
    })
  },
  watch: {
    selectedPaidFilter (val) {
      // prevent automatic call on first component load
      if (this.firstApiCalled) {
        if (val) {
          this.selectedVehicles = []
        }

        this.searchTransactions()
      }
    },
    selectedLogType () {
      if (this.firstApiCalled) {
        this.searchTransactions()
      }
    }
  },
  methods: {
    onSelectedAccountChanged () {
      this.selectedVehicles = []
      this.pagination.page = 1
    },
    onFilterByVehicle () {
      if (this.firstApiCalled) {
        this.pagination.page = 1
        this.searchTransactions()
      }
    },
    onPagination () {
      if (this.firstApiCalled) {
        this.searchTransactions()
      }
    },
    goToPreviousPage () {
      if (!this.canGoToPreviousPage) return
      this.pagination.page = Number(this.pagination.page) - 1
      this.searchTransactions()
    },
    goToNextPage () {
      if (!this.canGoToNextPage) return
      this.pagination.page = Number(this.pagination.page) + 1
      this.searchTransactions()
    },
    openAdHocRevenueDialog () {
      this.adhocRevenue = {}
      this.adhocRevenueDialog = true
    },
    updateAdHocRevenueDialog (o) {
      this.adhocRevenue = {
        ...o,
        notes: o.logMetadata?.notes || '',
        confirmationId: o.logMetadata?.confirmationId || ''
      }

      this.adhocRevenueDialog = true
    },
    goToPayout () {
      if (this.currentSelectedAccount) {
        this.$router.push({ name: 'transportation-accounts-payouts-payout', params: { payoutId: '0' }, query: { dateFrom: this.dateFrom || undefined, dateTo: this.dateTo || undefined } })
      } else {
        alert('Please select an account first')
      }
    },
    marshallTransaction (o) {
      o.dateCreatedLabel = `${formatTime(o.dateCreated)}, ${formatDate(o.dateCreated)}`
      o.amountLabel = formatCurrency(o.logType === 'purchased_ticket' ? o.amount * -1 : o.amount, this.countryCurrencies[o.countryCurrencyId]?.currencySymbol)
      o.payoutLabel = o.logStatus === 'expired' || o.logStatus === 'confirmed' ? (o.transportationUserTransactionLogId ? 'Paid' : 'Pending') : 'n/a'
      o.notesLabel = '<span>' + (o.logType === 'ad_hoc_revenue' ? ((o.logMetadata.notes || '') + (o.logMetadata.confirmationId ? `\nConfirmation ID: ${o.logMetadata.confirmationId}` : '')).replace(/\n/g, '<br>') : '') + '</span>'
      return o
    },
    async searchTransactions () {
      const requestId = (this.lastSearchRequestId || 0) + 1
      this.lastSearchRequestId = requestId

      const { sortBy, descending, page, rowsPerPage } = this.pagination
      const offset = page === 1 ? 0 : (page * rowsPerPage) - rowsPerPage

      let params = {
        filter: {
          limit: rowsPerPage,
          offset
        }
      }

      if (this.selectedPaidFilter === 'onlypaid') {
        params.filter.onlyPaid = true
      } else if (this.selectedPaidFilter === 'pendingpaid') {
        params.filter.onlyPending = true
      }

      if (this.selectedLogType) {
        params.filter.logType = this.selectedLogType
      }

      const accountId = this.currentSelectedAccount ? this.currentSelectedAccount.id : null
      const selectedVehicleIds = this.selectedVehicleIds

      if (!accountId && !selectedVehicleIds.length) {
        this.transactions = []
        this.totalTransactions = 0
        this.apiInProgress = false
        this.countInProgress = false
        return
      }

      if (accountId) {
        params.filter.transportationOwnerId = accountId
      }

      if (selectedVehicleIds.length) {
        params.filter.transportationProfileIds = selectedVehicleIds
        if (selectedVehicleIds.length === 1) {
          params.filter.transportationProfileId = selectedVehicleIds[0]
        }
      }

      params.filter.order = `${sortBy} ${descending ? 'DESC' : 'ASC'}`

      if (this.dateFrom) {
        params.filter.fromDate = moment.tz(this.dateFrom, 'Y-MM-DD', 'Africa/Accra').startOf('day').toDate().toISOString()
        // moment(this.dateFrom, 'Y-MM-DD').utc().toDate().toISOString()
      }

      if (this.dateTo) {
        params.filter.toDate = moment.tz(this.dateTo, 'Y-MM-DD', 'Africa/Accra').endOf('day').toDate().toISOString()
      }

      // Keep data and count aligned by applying the same default date range
      // when no explicit date filter is provided.
      if (!this.dateFrom && !this.dateTo) {
        const nowInAccra = moment.tz('Africa/Accra')
        params.filter.fromDate = nowInAccra.clone().subtract(60, 'days').startOf('day').toDate().toISOString()
        params.filter.toDate = nowInAccra.clone().endOf('day').toDate().toISOString()
      }

      params.filter.include = {
        relation: 'passengerTrips',
        scope: {
          limit: 1
        }
      }

      this.apiInProgress = true
      this.countInProgress = true
      // Reset total early so list rendering never waits on count.
      this.totalTransactions = null

      try {
        const transactions = await findTransactionsPayout(params.filter)

        if (this.lastSearchRequestId !== requestId) {
          return
        }

        this.transactions = transactions.map(o => {
          return this.marshallTransaction(o)
        })

        this.initTransportationOwnerNamesDisplay()

        this.apiInProgress = false

        // Fire-and-forget count so slow timeout does not block page rendering.
        findTotalTransactionsPayout({
          ...params.filter,
          requestTimeoutMs: REVENUE_COUNT_REQUEST_TIMEOUT_MS
        }).then((result) => {
          if (this.lastSearchRequestId !== requestId) return
          this.totalTransactions = result && typeof result.count === 'number' ? result.count : null
        }).catch(() => {
          if (this.lastSearchRequestId !== requestId) return
          this.totalTransactions = null
        }).finally(() => {
          if (this.lastSearchRequestId !== requestId) return
          this.countInProgress = false
        })
      } catch (err) {
        if (this.lastSearchRequestId !== requestId) {
          return
        }

        addAlert({ message: err, type: 'error' })

        this.apiInProgress = false
        this.countInProgress = false
      }
    },
    async initTransportationOwnerNamesDisplay () {
      try {
        if (this.transactions?.length) {
          this.currentTransportations = this.currentTransportations || []
          let transportationIdsToQuery = []

          this.transactions.forEach((transaction) => {
            let found
            const transportationProfileIdFromTransaction = transaction.transportationProfileId || transaction.passengerTrips[0].transportationProfileId

            for (let i = 0; i < this.currentTransportations.length; i++) {
              if (this.currentTransportations.find((o) => (o.transportationProfileId === transportationProfileIdFromTransaction))) {
                found = true
                break
              }
            }

            if (transportationProfileIdFromTransaction && !found) {
              transportationIdsToQuery.push(transportationProfileIdFromTransaction)
            }
          })

          transportationIdsToQuery = _uniq(transportationIdsToQuery)

          if (transportationIdsToQuery.length) {
            const transportationProfiles = await findTransportationProfiles({
              where: { id: { inq: transportationIdsToQuery } }
            })

            this.currentTransportations = this.currentTransportations.concat(transportationProfiles)
          }

          this.transactions = this.transactions.map((transaction, index) => {
            const transportationProfile = this.currentTransportations.find((o) => o.id === (transaction.transportationProfileId || transaction.passengerTrips[0].transportationProfileId))

            if (transportationProfile) {
              return { ...transaction, transportationProfileName: transportationProfile.licensePlateNumber }
            }

            return transaction
          })
        }
      } catch (err) {
        addAlert({ message: err, type: 'error' })
      }
    }
  },
  beforeDestroy () {
    if (this.selectedAccountWatchHandle) {
      this.selectedAccountWatchHandle()
    }
  }
}
